import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTheme } from "../contexts/ThemeContext"
import { isEmailSignInLink, completeEmailSignIn } from "../utils/firebase"
import { useErrorFeedback } from "../hooks/useErrorFeedback"
import { ErrorFeedback } from "../components/shared/ErrorFeedback"
import { useTranslation } from "react-i18next"

const LoadingSpinner: React.FC = () => {
  const { colors } = useTheme()
  return (
    <div className="flex justify-center items-center">
      <div
        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
        style={{ borderColor: colors.primary }}
      ></div>
    </div>
  )
}

export function EmailLinkSignInPage() {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(true)
  const { error, showError, clearError, isErrorVisible, componentName } = useErrorFeedback()

  useEffect(() => {
    const completeSignIn = async () => {
      try {
        const link = window.location.href
        if (!isEmailSignInLink(link)) {
          navigate("/login")
          return
        }

        // Get the email from localStorage
        const email = window.localStorage.getItem("emailForSignIn")
        if (!email) {
          showError(new Error(t('auth.emailLinkMissingEmail')), "EmailLinkSignInPage")
          return
        }

        await completeEmailSignIn(email, link)
        navigate("/today")
      } catch (error) {
        const message = error && typeof error === 'object' && 'code' in error && error.code === 'PRIVATE_APP_ACCESS'
          ? t('auth.privateAccessDenied')
          : error instanceof Error ? error.message : t('auth.emailLinkFailed')
        showError(new Error(message), "EmailLinkSignInPage")
      } finally {
        setIsProcessing(false)
      }
    }

    completeSignIn()
  }, [navigate, showError, t])

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-md w-full space-y-6 p-8 rounded-lg shadow-lg" style={{ backgroundColor: colors.surface }}>
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>
            {isProcessing ? t('auth.completingSignIn') : t('auth.signInComplete')}
          </h2>
          {isProcessing ? (
            <LoadingSpinner />
          ) : (
            !error && <p style={{ color: colors.textSecondary }}>{t('auth.redirectingShortly')}</p>
          )}
        </div>

        {error && (
          <ErrorFeedback error={error} componentName={componentName} isOpen={isErrorVisible} onClose={clearError} />
        )}
      </div>
    </div>
  )
}

export default EmailLinkSignInPage
